<template>
  <div :class="stylingClass">
    <label> {{ item.label }}<span v-if="item.required">*</span> </label>
    <validation-observer v-slot="{ valid, touched, errors: observerErrors }">
      <div class="fields-wrapper">
        <validation-provider
          rules="required"
          v-slot="{ invalid, touched, errors }"
        >
          <div class="day-picker select-box">
            <select
              v-model="selectedDay"
              :class="[
                { invalid: invalid },
                { touched: touched },
                { 'has-errors': errors.length > 0 },
              ]"
            >
              <option value="" selected disabled hidden>
                Day
              </option>
              <option
                v-for="(day, index) in 31"
                :key="index"
                :value="day.toString().padStart(2, '0')"
              >
                {{ day }}
              </option>
            </select>
          </div>
        </validation-provider>
        <validation-provider
          rules="required"
          v-slot="{ invalid, touched, errors }"
        >
          <div class="month-picker select-box">
            <select
              v-model="selectedMonth"
              :class="[
                { invalid: invalid },
                { touched: touched },
                { 'has-errors': errors.length > 0 },
              ]"
            >
              <option value="" selected disabled hidden>
                Month
              </option>
              <option
                v-for="(month, index) in 12"
                :key="index"
                :value="month.toString().padStart(2, '0')"
              >
                {{ months[index] }}
              </option>
            </select>
          </div>
        </validation-provider>
        <validation-provider
          rules="required"
          v-slot="{ invalid, touched, errors }"
        >
          <div class="year-picker select-box">
            <select
              v-model="selectedYear"
              :class="[
                { invalid: invalid },
                { touched: touched },
                { 'has-errors': errors.length > 0 },
              ]"
            >
              <option value="" selected disabled hidden>
                Year
              </option>
              <option v-for="year in yearsToRender" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
        </validation-provider>
      </div>
      <span
        v-if="
          (!valid && touched) ||
            Object.values(observerErrors)
              .flat()
              .some((x) => x)
        "
        class="validation-label"
      >
        This field is required
      </span>
      <span v-if="touched && valid && !isDateValid()" class="validation-label">
        Please select a valid date
      </span>
    </validation-observer>
  </div>
</template>

<script>
import { ref, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  props: ["item", "stylingClass"],
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  computed: {
    yearsToRender() {
      const years = [];
      let yearToAdd = this.minYear;
      while (yearToAdd <= this.maxYear) {
        years.push(yearToAdd);
        yearToAdd += 1;
      }

      return !this.item.orderYears
        ? years
        : years.sort((a, b) => Number(b) - Number(a));
    },
  },
  setup(props, { emit }) {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const selectedDay = ref(""),
      selectedMonth = ref(""),
      selectedYear = ref("");

    if (props.item.value) {
      const dateParts = props.item.value
        .substring(0, props.item.value.indexOf("T"))
        .split("-");
      selectedYear.value = dateParts[0];
      selectedMonth.value = dateParts[1];
      selectedDay.value = dateParts[2];
    }

    const config = {
      ...props.item.dateConfig,
      ...(props.item.dateConfig || {}),
    };

    let maxYear = new Date().getFullYear();
    if (config.maxDate) {
      maxYear = new Date(config.maxDate).getFullYear();
    }
    let minYear = 1900;
    if (config.minDate) {
      minYear = new Date(config.minDate).getFullYear();
    }

    watch([selectedDay, selectedMonth, selectedYear], () => {
      const valid = isDateValid();
      if (valid && selectedYear.value) {
        const datePicked = `${selectedYear.value}-${selectedMonth.value}-${selectedDay.value}T00:00:00Z`;
        emit("datePicked", datePicked, props.item.key);
      } else {
        emit("dateInvalid");
      }
    });

    const isDateValid = () => {
      const dateToCheck = `${selectedMonth.value}/${selectedDay.value}/${selectedYear.value}`;
      // Assumes s is "mm/dd/yyyy"
      if (!/^\d\d\/\d\d\/\d\d\d\d$/.test(dateToCheck)) {
        return false;
      }
      const parts = dateToCheck.split("/").map((p) => parseInt(p, 10));
      parts[0] -= 1;
      const d = new Date(parts[2], parts[0], parts[1]);

      if (props.item.disallowFutureDate) {
        return d < new Date();
      } else {
        return (
          d.getMonth() === parts[0] &&
          d.getDate() === parts[1] &&
          d.getFullYear() === parts[2]
        );
      }
    };

    return {
      isDateValid,
      maxYear,
      minYear,
      months,
      selectedDay,
      selectedMonth,
      selectedYear,
    };
  },
};
</script>

<style lang="scss" scoped>
.fields-wrapper {
  > * {
    display: block;
    margin-bottom: 16px;
  }

  @media all and (min-width: 1200px) {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
    justify-content: space-between;

    > * {
      flex-basis: calc((100% / 3) - 24px);
      width: calc((100% / 3) - 24px);
      margin-bottom: 0;
    }
  }
}
</style>
